import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Tranche, TrancheCondition, TrancheDrawdownSummary } from '@/types';

function invalidateLoanDerivedQueries(qc: ReturnType<typeof useQueryClient>, loanId: string) {
  qc.invalidateQueries({ queryKey: ['loans', loanId, 'tranches'] });
  qc.invalidateQueries({ queryKey: ['loans', loanId, 'drawdown-summary'] });
  qc.invalidateQueries({ queryKey: ['loans', loanId], exact: true });
  qc.invalidateQueries({ queryKey: ['loans', loanId, 'cashflows'], exact: true });
  qc.invalidateQueries({ queryKey: ['loans', loanId, 'npv'] });
  qc.invalidateQueries({ queryKey: ['loans', loanId, 'yield'], exact: true });
}

function normalizeCondition(raw: any): TrancheCondition {
  return {
    id: raw.id,
    trancheId: raw.trancheId ?? '',
    description: raw.description,
    completed: Boolean(raw.completed ?? raw.met),
    completedDate: raw.completedDate ?? raw.metDate ?? undefined,
    note: raw.note ?? undefined,
  };
}

function normalizeTranche(raw: any): Tranche {
  return {
    id: raw.id,
    loanId: raw.loanId,
    number: raw.number ?? raw.trancheNumber,
    amount: String(raw.amount),
    purpose: raw.purpose ?? undefined,
    status: raw.status,
    scheduledDate: raw.scheduledDate ?? undefined,
    drawnDate: raw.drawnDate ?? undefined,
    conditions: Array.isArray(raw.conditions) ? raw.conditions.map(normalizeCondition) : undefined,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
}

function normalizeDrawdownSummary(raw: any): TrancheDrawdownSummary {
  return {
    facilityLimit: Number(raw.facilityLimit ?? raw.totalCommitted ?? 0),
    totalCommitted: Number(raw.totalCommitted ?? 0),
    totalDrawn: Number(raw.totalDrawn ?? 0),
    remaining: Number(raw.remaining ?? raw.availableToDraw ?? 0),
    availableToDraw: Number(raw.availableToDraw ?? raw.remaining ?? 0),
    percentageUtilised: Number(raw.percentageUtilised ?? raw.percentage ?? 0),
    tranches: Array.isArray(raw.tranches) ? raw.tranches.map(normalizeTranche) : [],
  };
}

// Custom endpoint — keep manual
async function fetchTranches(loanId: string): Promise<Tranche[]> {
  const { data } = await api.get<any[]>(`/loans/${loanId}/tranches`);
  return data.map(normalizeTranche);
}

export function useTranches(loanId: string) {
  return useQuery({
    queryKey: ['loans', loanId, 'tranches'],
    queryFn: () => fetchTranches(loanId),
    enabled: !!loanId,
  });
}

// Custom endpoint /drawdown-summary — keep manual
async function fetchDrawdownSummary(loanId: string): Promise<TrancheDrawdownSummary> {
  const { data } = await api.get<any>(`/loans/${loanId}/tranches/summary`);
  return normalizeDrawdownSummary(data);
}

export function useDrawdownSummary(loanId: string) {
  return useQuery({
    queryKey: ['loans', loanId, 'drawdown-summary'],
    queryFn: () => fetchDrawdownSummary(loanId),
    enabled: !!loanId,
  });
}

interface CreateTrancheInput {
  amount: number;
  purpose?: string;
  scheduledDate?: string;
}

// Custom endpoint with loanId in params — keep manual
async function createTranche(loanId: string, input: CreateTrancheInput): Promise<Tranche> {
  const { data } = await api.post<any>(`/loans/${loanId}/tranches`, input);
  return normalizeTranche(data);
}

export function useCreateTranche() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loanId, ...input }: CreateTrancheInput & { loanId: string }) =>
      createTranche(loanId, input),
    onSuccess: (_data, vars) => {
      invalidateLoanDerivedQueries(qc, vars.loanId);
    },
  });
}

interface UpdateTrancheInput {
  amount?: number;
  purpose?: string;
  scheduledDate?: string;
}

// Custom endpoint with loanId in params — keep manual
async function updateTranche(
  loanId: string,
  trancheId: string,
  input: UpdateTrancheInput,
): Promise<Tranche> {
  const { data } = await api.patch<any>(`/loans/${loanId}/tranches/${trancheId}`, input);
  return normalizeTranche(data);
}

export function useUpdateTranche() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      loanId,
      trancheId,
      ...input
    }: UpdateTrancheInput & { loanId: string; trancheId: string }) =>
      updateTranche(loanId, trancheId, input),
    onSuccess: (_data, vars) => {
      invalidateLoanDerivedQueries(qc, vars.loanId);
    },
  });
}

// Custom action endpoint /approve — keep manual
async function approveTranche(loanId: string, trancheId: string): Promise<Tranche> {
  const { data } = await api.post<any>(`/loans/${loanId}/tranches/${trancheId}/approve`);
  return normalizeTranche(data);
}

export function useApproveTranche() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loanId, trancheId }: { loanId: string; trancheId: string }) =>
      approveTranche(loanId, trancheId),
    onSuccess: (_data, vars) => {
      invalidateLoanDerivedQueries(qc, vars.loanId);
    },
  });
}

interface DrawTrancheInput {
  amount?: number;
}

// Custom action endpoint /draw — keep manual
async function drawTranche(
  loanId: string,
  trancheId: string,
  input?: DrawTrancheInput,
): Promise<Tranche> {
  const { data } = await api.post<any>(`/loans/${loanId}/tranches/${trancheId}/draw`, input || {});
  return normalizeTranche(data);
}

export function useDrawTranche() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      loanId,
      trancheId,
      ...input
    }: (DrawTrancheInput | undefined) & { loanId: string; trancheId: string }) =>
      drawTranche(loanId, trancheId, input),
    onSuccess: (_data, vars) => {
      invalidateLoanDerivedQueries(qc, vars.loanId);
    },
  });
}

// Custom action endpoint /cancel — keep manual
async function cancelTranche(loanId: string, trancheId: string): Promise<Tranche> {
  const { data } = await api.delete<any>(`/loans/${loanId}/tranches/${trancheId}`);
  return normalizeTranche(data);
}

export function useCancelTranche() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loanId, trancheId }: { loanId: string; trancheId: string }) =>
      cancelTranche(loanId, trancheId),
    onSuccess: (_data, vars) => {
      invalidateLoanDerivedQueries(qc, vars.loanId);
    },
  });
}

// Custom endpoint for conditions — keep manual
async function addTrancheCondition(
  loanId: string,
  trancheId: string,
  description: string,
): Promise<TrancheCondition> {
  const { data } = await api.post<any>(`/loans/${loanId}/tranches/${trancheId}/conditions`, {
    description,
  });
  return normalizeCondition(data);
}

export function useAddTrancheCondition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      loanId,
      trancheId,
      description,
    }: {
      loanId: string;
      trancheId: string;
      description: string;
    }) => addTrancheCondition(loanId, trancheId, description),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['loans', vars.loanId, 'tranches'] });
    },
  });
}

// Custom endpoint /complete — keep manual
async function completeTrancheCondition(
  loanId: string,
  trancheId: string,
  conditionId: string,
  note?: string,
): Promise<TrancheCondition> {
  const { data } = await api.patch<any>(
    `/loans/${loanId}/tranches/${trancheId}/conditions/${conditionId}/complete`,
    { note },
  );
  return normalizeCondition(data);
}

export function useCompleteTrancheCondition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      loanId,
      trancheId,
      conditionId,
      note,
    }: {
      loanId: string;
      trancheId: string;
      conditionId: string;
      note?: string;
    }) => completeTrancheCondition(loanId, trancheId, conditionId, note),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['loans', vars.loanId, 'tranches'] });
    },
  });
}
